import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Button } from "@mui/material";
import { useSelector } from "react-redux";
import { useGetUserDetailsQuery } from "features/api/userApi.ts";
import { RootState } from "stores/application.store";
import { CabinetworksGroupApplication } from "types/declarations/enums.ts";
import { useGetDashboardActionItemCountQuery, useGetDashboardUnassignedLeadCountQuery } from "../api/dashboardApi";
import { Badge, CountState } from "../Dashboard.styles";
import { DashboardDestinations } from "../Dashboard.types";
import { useDashboardNavigation } from "../hooks/useDashboardNavigation";
import { DashboardCard } from "./DashboardCard";

const alertsIllustrationUrl = new URL("../assets/alerts-card.svg", import.meta.url).href;

interface AlertsCardProps {
	destinations: Pick<DashboardDestinations, "actionItems" | "news" | "launches">;
}

export const AlertsCard = ({ destinations }: AlertsCardProps) => {
	const goTo = useDashboardNavigation();
	const impersonation = useSelector((state: RootState) => state.impersonation.impersonationUser);
	const { data: user } = useGetUserDetailsQuery();
	const contextKey = JSON.stringify({
		user: user?.email,
		emulatedEmail: impersonation?.email,
		emulatedAccount: impersonation?.accountId
	});
	const actionItems = useGetDashboardActionItemCountQuery(
		{ contextKey },
		{ pollingInterval: 60_000, refetchOnFocus: true, refetchOnMountOrArgChange: true }
	);
	const leads = useGetDashboardUnassignedLeadCountQuery(
		{ contextKey },
		{ pollingInterval: 60_000, refetchOnFocus: true, refetchOnMountOrArgChange: true }
	);
	const countsReady = actionItems.currentData !== undefined && leads.currentData !== undefined;
	const hasError = actionItems.isError || leads.isError;
	const total = countsReady ? actionItems.currentData! + leads.currentData! : undefined;
	const retry = () => {
		actionItems.refetch();
		leads.refetch();
	};
	const tandemDestination = (href: string) => ({ href, application: CabinetworksGroupApplication.Tandem });
	const countLabel = (label: string, count: number | undefined, color: string) => (
		<span>
			{label} {count !== undefined && <Badge color={color}>{count}</Badge>}
		</span>
	);
	let countStatus = null;
	if (hasError) {
		countStatus = (
			<CountState role="alert">
				Counts unavailable. <Button onClick={retry}>Retry</Button>
			</CountState>
		);
	} else if (!countsReady) {
		countStatus = <CountState aria-live="polite">Loading alert counts…</CountState>;
	}

	return (
		<DashboardCard
			testId="dashboard-alerts-card"
			title={
				<>
					Alerts{" "}
					{total !== undefined && (
						<Badge
							color="#dc582a"
							aria-label={`${total} total alerts`}
						>
							{total}
						</Badge>
					)}
				</>
			}
			illustration={
				<img
					src={alertsIllustrationUrl}
					alt=""
					aria-hidden="true"
				/>
			}
			accent="#dc582a"
			titleColor="#dc582a"
			status={countStatus}
			actions={[
				{
					id: "action-items",
					label: countLabel("Action Items", actionItems.currentData, "#dc582a"),
					ariaLabel: `Action Items${actionItems.currentData !== undefined ? `, ${actionItems.currentData}` : ""}`,
					icon: <WarningAmberOutlinedIcon aria-hidden="true" />,
					color: "#dc582a",
					onClick: () => goTo(destinations.actionItems)
				},
				{
					id: "leads",
					label: countLabel("Leads", leads.currentData, "#006594"),
					ariaLabel: `Leads${leads.currentData !== undefined ? `, ${leads.currentData}` : ""}`,
					icon: <BadgeOutlinedIcon aria-hidden="true" />,
					onClick: () => goTo(tandemDestination("/lead-assignments"))
				},
				{
					id: "news",
					label: "News",
					icon: <ArticleOutlinedIcon aria-hidden="true" />,
					onClick: () => goTo(destinations.news)
				},
				{
					id: "launches",
					label: "Launches",
					icon: <CelebrationOutlinedIcon aria-hidden="true" />,
					onClick: () => goTo(destinations.launches)
				}
			]}
		/>
	);
};
